import { useState, useRef, useEffect } from "react";
import Collapse from "../../components/UI/Collapse";
import { createPortal } from "react-dom";
import { useBotPhotos } from "../../hooks/useBotPhotos";
import BotChip from "../../components/BotChip/BotChip";
import { useNotification } from "../../components/Notifications/NotificationContext";
import { MoreVertical, Pencil, ExternalLink, BarChart3, Trash2, Megaphone, Copy, Link, Link2, Eye } from "lucide-react";
import "../Tracking/botselector.css";
import "./RedirectList.css";

const BRL = (v) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const num = (v) => new Intl.NumberFormat("pt-BR").format(v || 0);

export function RowMenu({ onEdit, onOpenLink, onStats, onInterstitial, onDelete }) {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState(null);
    const btnRef = useRef(null);
    const menuRef = useRef(null);

    const MENU_W = 200;

    const place = () => {
        const b = btnRef.current?.getBoundingClientRect();
        if (!b) return;
        setCoords({ top: b.bottom + 4, left: b.right - MENU_W });
    };

    const toggle = () => {
        if (!open) place();
        setOpen((v) => !v);
    };

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        const reposition = () => setOpen(false);
        document.addEventListener("mousedown", handler);
        window.addEventListener("scroll", reposition, true);
        window.addEventListener("resize", reposition);
        return () => {
            document.removeEventListener("mousedown", handler);
            window.removeEventListener("scroll", reposition, true);
            window.removeEventListener("resize", reposition);
        };
    }, [open]);

    const item = (icon, label, fn, danger) => (
        <button
            onClick={() => { setOpen(false); fn(); }}
            className={`flex items-center gap-2 w-full px-3 py-2 text-sm text-left transition hover:bg-white/[0.06] ${danger ? "text-[#f87171]" : "text-white/80"}`}
        >
            {icon}{label}
        </button>
    );

    return (
        <div className="relative">
            <button ref={btnRef} className="action-btn" onClick={toggle} aria-label="Mais opções">
                <MoreVertical size={16} />
            </button>
            {open && coords && createPortal(
                <div
                    ref={menuRef}
                    style={{ position: "fixed", top: coords.top, left: coords.left, width: MENU_W }}
                    className="rounded-xl border border-white/[0.1] bg-[#161b24] shadow-[0_16px_40px_rgba(0,0,0,0.5)] py-1 z-[999]"
                >
                    {item(<Pencil size={14} />, "Editar", onEdit)}
                    {item(<ExternalLink size={14} />, "Abrir link", onOpenLink)}
                    {item(<BarChart3 size={14} />, "Ver estatísticas", onStats)}
                    {onInterstitial && item(<Eye size={14} />, "Página intermediária", onInterstitial)}
                    <div className="my-1 h-px bg-white/[0.08]" />
                    {item(<Trash2 size={14} />, "Excluir", onDelete, true)}
                </div>,
                document.body
            )}
        </div>
    );
}

function CloakerStats({ m, loading = false }) {
    if (loading) return <span className="inline-block w-32 h-2.5 rounded bg-white/[0.08] animate-pulse" />;
    if (!m) return null;
    return (
        <div className="flex items-center gap-2 text-[10px] leading-none text-white/50">
            <span className="inline-flex items-center gap-1 text-[#f87171]" title="Bloqueados">
                <i className="fas fa-shield-alt text-[9px]" /> {num(m.bloqueados)}
            </span>
            <span className="inline-flex items-center gap-1 text-[#4ade80]" title="Aprovados">
                <i className="fas fa-check text-[9px]" /> {num(m.aprovados)}
            </span>
            <span>{m.aprovacao_pct}% aprov.</span>
        </div>
    );
}

function Metrics({ m, grid = false, loading = false }) {
    const cells = loading ? ["Cliques", "Vendas", "Receita", "Conversão"].map((label) => ({
        label, value: <span className="inline-block w-10 h-3.5 rounded bg-white/[0.08] animate-pulse align-middle" />, cls: "",
    })) : [
        { label: "Cliques", value: m ? num(m.cliques) : "—", cls: "text-white" },
        { label: "Vendas", value: m ? num(m.vendas) : "—", cls: "text-white" },
        { label: "Receita", value: m ? BRL(m.receita) : "—", cls: "text-[#4ade80]" },
        { label: "Conversão", value: m ? `${m.conversao_pct}%` : "—", cls: "text-[#60a5fa]" },
    ];

    // Variante em cards (2x2 no mobile, 4 colunas no desktop empilhado), alinhado à esquerda
    if (grid) {
        return (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {cells.map((c) => (
                    <div key={c.label} className="rounded-xl bg-white/[0.02] border border-white/[0.05] px-3 py-2 text-left min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-white/35 truncate">{c.label}</div>
                        <div className={`text-sm font-bold truncate ${c.cls}`}>{c.value}</div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="flex items-center rounded-lg bg-white/[0.02] border border-white/[0.05] divide-x divide-white/[0.05]">
            {cells.map((c) => (
                <div key={c.label} className="flex-1 px-2.5 py-2 text-center min-w-0">
                    <div className="text-[9px] uppercase tracking-wide text-white/35 truncate">{c.label}</div>
                    <div className={`text-sm font-bold truncate ${c.cls}`}>{c.value}</div>
                </div>
            ))}
        </div>
    );
}

export default function RedirectList({
    redirects, bots = [], metricsMap = {}, showMetrics = false, metricsLoading = false, secondaryApiUrl,
    onDelete, onEdit, onCreateNew, onOpenStats, onOpenCampaign, onOpenInterstitial,
}) {
    const photoUrls = useBotPhotos(bots);
    const { showSuccess } = useNotification();

    const PAGE_SIZE = 10;
    const [page, setPage] = useState(1);
    const totalPages = Math.max(1, Math.ceil(redirects.length / PAGE_SIZE));
    useEffect(() => { setPage((p) => Math.min(p, totalPages)); }, [totalPages]);
    const pageItems = redirects.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const copy = (text) => {
        navigator.clipboard.writeText(text);
        showSuccess("Link copiado para a área de transferência.");
    };

    const getBotEntries = (r) =>
        r.bot_ids?.map((botId) => {
            const bot = bots?.find((b) => b.id === botId);
            return { botId, label: bot ? (bot.bot_username || bot.bot_name) : `bot${botId}` };
        }) || [];

    if (redirects.length === 0) {
        return (
            <div className="empty-state" style={{ textAlign: "center", padding: "2em" }}>
                <Link size={32} className="mx-auto mb-4 text-white/30" />
                <h4>Nenhum redirecionador encontrado</h4>
                <p style={{ color: "#666", marginBottom: "1.5em" }}>
                    Ajuste a busca/filtros ou crie um novo redirecionador.
                </p>

            </div>
        );
    }

    return (
        <>
        <div className="rounded-2xl border border-white/[0.08] overflow-hidden">
            {pageItems.map((r, idx) => {
                const botEntries = getBotEntries(r);
                // Link já com o cloaker (sx) quando existir, para copiar/abrir funcionando.
                const url = `${secondaryApiUrl}/r/${r.slug}${r.cloaker_param ? `?sx=${r.cloaker_param}` : ""}`;
                const m = metricsMap[r.slug];

                return (
                    <div
                        key={r.slug}
                        className={`redir-row p-4 lg:px-5 lg:py-4 ${idx > 0 ? "border-t border-white/[0.06]" : ""} ${r.active ? "" : "opacity-60"}`}
                    >
                        {/* ===== Desktop: 1280px+ em linha · 1024–1279px empilhado · abaixo: versão de tela menor ===== */}
                        <div className="hidden lg:flex lg:flex-col xl:flex-row xl:items-center gap-4 xl:gap-5">
                            {/* Link: ícone + nome + url */}
                            <div className="flex items-start gap-3 min-w-0 xl:w-[320px] xl:flex-shrink-0">
                                <div className="w-10 h-10 rounded-xl bg-[rgba(139,92,246,0.12)] border border-[rgba(139,92,246,0.18)] flex items-center justify-center text-[#a78bfa] flex-shrink-0">
                                    <Link2 size={18} />
                                </div>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="redir-slug font-semibold text-white truncate" title={r.slug}>{r.slug}</span>
                                        {r.has_cloaker && <span className="sx-pill sx-pill--blue">Cloaker</span>}
                                    </div>
                                    <a href={url} target="_blank" rel="noreferrer" className="sx-link redir-url text-sm truncate block max-w-full" title={url}>
                                        {url}
                                    </a>
                                    <Collapse open={showMetrics} innerClassName="pt-1.5">
                                        <CloakerStats m={m} loading={metricsLoading} />
                                    </Collapse>
                                </div>
                            </div>

                            {/* Entrega (bots) — um embaixo do outro; ao lado do nome a partir de xl */}
                            <div className="min-w-0 xl:flex-1 border-t border-white/[0.06] pt-4 xl:border-t-0 xl:pt-0">
                                <div className="flex flex-col items-start gap-1.5">
                                    {botEntries.length > 0
                                        ? botEntries.map(({ botId, label }) => (
                                            <BotChip key={botId} photoUrl={photoUrls[botId]} label={label} size={20} textSize={14} />
                                        ))
                                        : <span className="text-white/30 text-xs">sem bot</span>}
                                </div>

                            </div>

                            {/* Métricas — barra compacta (divide-x, sem gap), só com "Ver métricas".
                                Fechada, devolve o gap do pai (-mt-4 empilhado / -ml-5 em linha). */}
                            <Collapse
                                open={showMetrics}
                                className="xl:flex-shrink-0"
                                openClassName="xl:w-[360px]"
                                closedClassName="-mt-4 xl:mt-0 xl:w-0 xl:-ml-5"
                            >
                                <Metrics m={m} loading={metricsLoading} />
                            </Collapse>

                            {/* Ações */}
                            <div className="flex items-center gap-2 xl:flex-shrink-0">
                                <button className="btn-secondary btn-action w-2/5" onClick={() => copy(url)} title="Copiar">
                                    <Copy size={14} /> Copiar
                                </button>
                                <button className="btn-primary btn-action flex-1 xl:flex-none justify-center" onClick={() => onOpenCampaign(r)}>
                                    <Megaphone size={14} /> Preparar campanha
                                </button>
                                <RowMenu
                                    onEdit={() => onEdit(r)}
                                    onOpenLink={() => window.open(url, "_blank")}
                                    onStats={() => onOpenStats(r)}
                                    onInterstitial={onOpenInterstitial ? () => onOpenInterstitial(r) : undefined}
                                    onDelete={() => onDelete(r.slug)}
                                />
                            </div>
                        </div>

                        {/* ===== Tela menor (< 1024px): bloco ===== */}
                        <div className="lg:hidden">
                            <div className="flex items-start justify-between gap-3 mb-2">
                                <div className="flex items-start gap-3 min-w-0">
                                    <div className="w-10 h-10 rounded-xl bg-[rgba(139,92,246,0.12)] border border-[rgba(139,92,246,0.18)] flex items-center justify-center text-[#a78bfa] flex-shrink-0">
                                        <Link2 size={18} />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="redir-slug font-semibold text-white truncate" title={r.slug}>{r.slug}</span>
                                            {r.has_cloaker && <span className="sx-pill sx-pill--blue">Cloaker</span>}
                                        </div>
                                        <a href={url} target="_blank" rel="noreferrer" className="sx-link redir-url text-sm break-all block">
                                            {url}
                                        </a>
                                    </div>
                                </div>
                                <RowMenu
                                    onEdit={() => onEdit(r)}
                                    onOpenLink={() => window.open(url, "_blank")}
                                    onStats={() => onOpenStats(r)}
                                    onInterstitial={onOpenInterstitial ? () => onOpenInterstitial(r) : undefined}
                                    onDelete={() => onDelete(r.slug)}
                                />
                            </div>

                            <Collapse open={showMetrics} innerClassName="pb-4">
                                <CloakerStats m={m} loading={metricsLoading} />
                            </Collapse>



                            {botEntries.length > 0 && (
                                <div className="flex flex-col items-start gap-1.5 mb-4">
                                    {botEntries.map(({ botId, label }) => (
                                        <BotChip key={botId} photoUrl={photoUrls[botId]} label={label} size={20} textSize={14} />
                                    ))}
                                </div>
                            )}


                            <Collapse open={showMetrics} innerClassName="pb-3">
                                <Metrics m={m} grid loading={metricsLoading} />
                            </Collapse>

                            <div className="flex items-center gap-2">
                                <button className="btn-secondary btn-action flex-1 justify-center" onClick={() => copy(url)} title="Copiar">
                                    <Copy size={14} /> Copiar
                                </button>
                                <button className="btn-primary btn-action flex-1 justify-center" onClick={() => onOpenCampaign(r)}>
                                    <Megaphone size={14} /> Preparar campanha
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>

        {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-between">
                <div className="text-sm text-white/40">
                    Página {page} de {totalPages} ({redirects.length} redirecionadores)
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="btn-secondary"
                        style={{ opacity: page === 1 ? 0.5 : 1 }}
                    >
                        <i className="fas fa-chevron-left" /> Anterior
                    </button>
                    <button
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="btn-primary"
                        style={{ opacity: page === totalPages ? 0.5 : 1 }}
                    >
                        Próximo <i className="fas fa-chevron-right" />
                    </button>
                </div>
            </div>
        )}
        </>
    );
}
